import {defineStore} from "pinia";
import {
    useAppState,
    useAppState as useAppStateStore,
} from "../../../Stores/AppState";
import {useWorkspace} from "../../../Stores/Workspaces";
import {useConversation} from "../../../Stores/Conversation";
import {useAuthorityLevelStore} from "../../../Stores/AuthorityLevel";
import {openMembershipTabForConversation} from "../../Custom/Team/Composables/useWorkPanelMembershipConversation";
import {
    MESSAGE_COMPOSER_MAX_HEIGHT_PX,
    MESSAGE_COMPOSER_MIN_HEIGHT_PX,
    MESSAGE_INPUT_SCROLLABLE_CLASS,
    messageComposerChromeHeight,
    resolveMessageComposerHeight,
} from "../Helpers/messageInputScrollPadding.js";
import {
    pinnedMessagesScroll,
    restorePinnedMessagesScroll,
} from "../Helpers/pinnedMessagesScroll.js";

let scheduled_composer_adjust_frame = null;

export const useChat = defineStore("Chat", {
    state: () => ({
        message: null,
        tab: "message",
        attached_files: null,
        annotated_file: null,
        is_message_tiptap_clear: false,
        message_reply_to: null,
        /** Captured when opening a reply so footer growth can restore bottom pin. */
        messages_scroll_pin_snapshot: null,
        is_to_focus: false,
        active_update: null,
        active_update_message: null,
        is_user_typing: false,
        is_annotating: null,
        is_annotated: null,
        annotating_conversation_sid: null,
        active_conv_sid: null,
        newly_added_message_total_height: 0,
        is_scroll_disabled: false,
        chat_stream_loader: true,
        activity_textbox_height: 112,
        is_activity_dragged: false,
        /** Dragged composer height. Content can grow past it, up to the maximum. */
        composer_height_floor: null,
        is_message_editor_focus: false,
        is_checklist_active: false,
        topbar_avatar_active_menu: null,
        urgent_request: false,
        request_support_user: null,
        previous_tab: "message",
        member_panel_tab_before_open_membership: null,
        conv_id_before_open_membership: null,
        /** Saved when opening standalone associate dashboard/settings for X-close restore. */
        conv_id_before_standalone_associate: null,
        membership_prefetched_conversation_id: null,
        is_membership_data_loading: false,
        is_empty: true,
        is_loading: false,
        message_activity: [],
        pending_forward_html: null,
        pending_forward_conversation_id: null,
        dashboard_default_data: null,
        standalone_associate_profile_id: null,
        associate_dashboard: {
            loading: false,
            data: {},
        },
        is_fetching_associate_dashboard: false,
        is_fetching_associate_settings: false,
        /** When set, pyramid toolbar shows loading; panel skips full-screen loader for this profile id only. */
        worker_settings_toolbar_open_profile_id: null,
        /** True while WorkerSettings/Index is hydrating form data (see chin-state). */
        worker_settings_data_loading: false,
        /** True while Associate Settings → Reports To sub-view is open; chat popover hides main header and chin. */
        worker_settings_reports_to_active: false,
    }),
    getters: {
        is_comment_tab: (state) => state.tab === "comment",
        is_member_tab: (state) => state.tab === "member",
        is_new_member_tab: (state) => state.tab === "new_member",
        is_new_workpanel_member_tab: (state) =>
            state.tab === "new_workpanel_member",
        is_message_tab: (state) => state.tab === "message",
        is_dashboard_tab: (state) => state.tab === "dashboard",
        is_worker_settings_tab: (state) => state.tab === "worker_settings",
    },
    actions: {
        setMessage(val) {
            this.message = val;
        },

        resetMessage() {
            this.setMessage(null);
        },

        setTab(val) {
            if (this.tab === "worker_settings" && val !== "worker_settings") {
                this.worker_settings_toolbar_open_profile_id = null;
                this.worker_settings_reports_to_active = false;
                this.worker_settings_data_loading = false;
            }
            this.tab = val;

            if (this.tab === "message") {
                //useConversation().active?.markAsReadIfCorrectWindowOpenForMessageRead();
            }
        },

        setCommentTab() {
            this.setTab("comment");
        },

        setMessageTab() {
            this.setTab("message");
        },

        setMemberTab() {
            if (this.tab === "member") {
                this.member_panel_tab_before_open_membership =
                    useAppState().selected_variant_tab;
            }
            this.setTab("member");
        },

        setNewMemberTab() {
            this.setTab("new_member");
        },

        toggleNewMemberTab() {
            this.setTab(this.tab === "new_member" ? "message" : "new_member");
        },

        setNewWorkPanelMemberTab() {
            this.setTab("new_workpanel_member");
        },

        toggleNewWorkPanelMemberTab() {
            this.setTab(
                this.tab === "new_workpanel_member"
                    ? "message"
                    : "new_workpanel_member",
            );
        },

        setWorkerSettingsTab() {
            this.setTab("worker_settings");
        },

        beginWorkerSettingsOpenFromToolbar(profileId) {
            this.worker_settings_toolbar_open_profile_id = profileId ?? null;
        },

        clearWorkerSettingsToolbarOpenProfileId() {
            this.worker_settings_toolbar_open_profile_id = null;
        },

        setWorkerSettingsDataLoading(isLoading) {
            this.worker_settings_data_loading = Boolean(isLoading);
        },

        setWorkerSettingsReportsToActive(isActive) {
            this.worker_settings_reports_to_active = Boolean(isActive);
        },

        closeEmbeddedWorkerSettings() {
            if (this.tab !== "worker_settings") {
                return;
            }
            this.is_fetching_associate_settings = false;
            this.worker_settings_data_loading = false;
            if (this.standalone_associate_profile_id) {
                const standaloneId = this.standalone_associate_profile_id;
                this.clearStandaloneAssociateProfileId();
                this.clearAssociateDashboard();
                this.clearConvIdBeforeStandaloneAssociate();
                const auth = useAuthorityLevelStore();
                if (auth.selectedProfileId == standaloneId) {
                    auth.setSelectedProfileId(null);
                }
            }
            this.setMessageTab();
        },

        toggleMemberTab() {
            if (this.tab === "member") {
                this.member_panel_tab_before_open_membership =
                    useAppState().selected_variant_tab;
            }

            this.setTab(this.tab === "member" ? "message" : "member");
        },

        setDashboardTab() {
            this.setTab("dashboard");
        },

        setDashboardDefaultData(data) {
            this.dashboard_default_data = data;
        },

        setStandaloneAssociateProfileId(profileId) {
            this.standalone_associate_profile_id = profileId;
        },

        clearStandaloneAssociateProfileId() {
            this.standalone_associate_profile_id = null;
        },

        /** Call before switching to another conversation (DM/item/group/load-by-id, etc.). */
        resetStandaloneAssociateSessionIfNeeded() {
            if (!this.standalone_associate_profile_id) return;
            this.is_fetching_associate_settings = false;
            this.worker_settings_data_loading = false;
            this.clearStandaloneAssociateProfileId();
            this.clearAssociateDashboard();
            this.clearConvIdBeforeStandaloneAssociate();
            const auth = useAuthorityLevelStore();
            this.setMessageTab();
        },

        clearAssociateDashboard() {
            this.associate_dashboard.data = {};
            this.associate_dashboard.loading = false;
        },

        async fetchAssociateDashboard(userId, {signal} = {}) {
            if (!userId) return;
            const uri = `/user-profiles/${userId}/associate-dashboard`;
            try {
                this.associate_dashboard.loading = true;
                const response = await axios.get(uri, {signal});
                this.associate_dashboard.data = {};
                this.associate_dashboard.data = response.data?.data ?? {};
            } catch (error) {
                // Re-throw cancelled requests so the caller can handle them
                // via isRequestCancelled(); only log genuine failures.
                if (error?.name === "CanceledError" || error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
                    throw error;
                }
                console.error("Error fetching associate dashboard", error);
            } finally {
                this.associate_dashboard.loading = false;
            }
        },

        attachFiles(val) {
            this.attached_files = val;
        },

        annotatedFile(val) {
            this.annotated_file = val;
        },

        resetAttachFiles() {
            this.attachFiles(null);
            this.annotatedFile(null);
        },

        scheduleAdjustTextBox() {
            if (scheduled_composer_adjust_frame !== null) {
                return;
            }

            scheduled_composer_adjust_frame = requestAnimationFrame(() => {
                scheduled_composer_adjust_frame = null;

                if (this.is_activity_dragged) {
                    return;
                }

                this.adjustTextBox();
            });
        },

        adjustTextBox(value = undefined) {
            const input = document.getElementById("new-message-input");
            const target =
                input?.closest(".prospus-text-area-wrap") ??
                document.querySelector(".prospus-text-area-wrap");

            if (!target) {
                return;
            }

            const is_message_composer =
                Boolean(input) &&
                target.contains(input) &&
                !target.classList.contains(
                    "prospus-chat-body-activity-area--comment",
                );

            if (!is_message_composer) {
                const height =
                    value === undefined ? this.getTextAreaHeight() : value;
                target.style.height = height + "px";
                this.activity_textbox_height = target.clientHeight + 53;

                return;
            }

            if (value === null) {
                this.composer_height_floor = null;
            } else if (this.is_activity_dragged && typeof value === "number") {
                this.composer_height_floor = value;
            }

            const prose_mirror = input.querySelector(
                "#editor-workspace-textarea .ProseMirror",
            );
            const resolved = resolveMessageComposerHeight({
                contentHeight: measureProseMirrorContentHeight(prose_mirror),
                chromeHeight: messageComposerChromeHeight(target, prose_mirror),
                minHeight: readCssPx(
                    target,
                    "minHeight",
                    MESSAGE_COMPOSER_MIN_HEIGHT_PX,
                ),
                maxHeight: readCssPx(
                    target,
                    "maxHeight",
                    MESSAGE_COMPOSER_MAX_HEIGHT_PX,
                ),
                preferredHeight: this.composer_height_floor,
            });

            const next_height = resolved.height + "px";
            const has_scrollable_class = target.classList.contains(
                MESSAGE_INPUT_SCROLLABLE_CLASS,
            );

            const messages_scroll = composerLayoutMessagesScrollSnapshot(this);

            if (
                target.style.height === next_height &&
                has_scrollable_class === resolved.scrollable
            ) {
                restorePinnedMessagesScroll(messages_scroll);

                return;
            }
            this.applyComposerHeight(target, resolved.height);
            target.classList.toggle(
                MESSAGE_INPUT_SCROLLABLE_CLASS,
                resolved.scrollable,
            );
            restorePinnedMessagesScroll(messages_scroll);
        },

        applyComposerHeight(target, height) {
            target.style.height = height + "px";
            this.activity_textbox_height = target.clientHeight + 53;
        },

        getTextAreaHeight(
            selector = "#editor-workspace-textarea .ProseMirror",
        ) {
            const element = document.querySelector(selector);
            const prev_scroll_height = element.scrollHeight;

            element.classList.add("removed-height");

            const current_scroll_height = element.scrollHeight;
            let final_scrollHeight = prev_scroll_height;

            if (prev_scroll_height - 9 < current_scroll_height)
                final_scrollHeight =
                    current_scroll_height === 42
                        ? current_scroll_height
                        : current_scroll_height + 19;

            setTimeout(() => {
                element.classList.remove("removed-height");
            }, 1);
            return final_scrollHeight + 18;
        },

        setMessageTiptapClear(val) {
            this.is_message_tiptap_clear = val;
        },

        async openConversation(force = true) {
            const app_state_store = useAppStateStore();
            if (app_state_store.worklineFilterDrawer) {
                app_state_store.worklineFilterDrawer = false;
            }
            const workspace_store = useWorkspace();

            const conversation_store = useConversation();
            const conversation = conversation_store.active;

            if (conversation?.isToShowHint() && !force) {
                const didOpenMembership = await openMembershipTabForConversation(
                    this,
                    conversation,
                );

                if (didOpenMembership) {
                    app_state_store.toggleInviteDrawer(
                        true,
                        workspace_store.active_chat_data,
                    );
                }
            } else {
                this.setMessageTab();
                this.setFocusOnEditor();
            }
        },

        setMessageReplyTo(message) {
            if (message) {
                this.messages_scroll_pin_snapshot = pinnedMessagesScroll();
            } else {
                this.messages_scroll_pin_snapshot = null;
            }

            this.message_reply_to = message;
        },

        restorePinnedMessagesAfterFooterLayout() {
            restorePinnedMessagesScroll(
                composerLayoutMessagesScrollSnapshot(this),
            );
        },

        setPendingForwardHtml(html, conversation_id = null) {
            this.pending_forward_html = html || null;
            this.pending_forward_conversation_id = conversation_id || null;
        },

        clearPendingForwardHtml() {
            this.pending_forward_html = null;
            this.pending_forward_conversation_id = null;
        },

        setFocusOnEditor(val = true) {
            this.is_to_focus = val;
        },

        setActiveMessageUpdate(val) {
            this.active_update = val;
        },

        setActiveMessageUpdateMessage(val) {
            this.active_update_message = val;
        },

        setUserTyping(val) {
            this.is_user_typing = val;
        },

        setAnnotated(val) {
            this.is_annotated = val;
        },

        setAnnotating(val, conversationSid = null) {
            this.is_annotating = val;
            if (val !== null && conversationSid !== null) {
                this.annotating_conversation_sid = conversationSid;
            }
        },

        clearAnnotatingConversationSid() {
            this.annotating_conversation_sid = null;
        },

        resetTextAreaHeight() {
            this.composer_height_floor = null;
            const input = document.getElementById("new-message-input");

            if (input) {
                input.style.setProperty("height", null);
                input.style.setProperty("overfolow", null);
            }

            this.adjustTextBox(null);
        },

        setActiveConvSid(val) {
            this.active_conv_sid = val;
        },

        resetTopbarActivity() {
            this.is_annotated = null;
            this.setMessageReplyTo(null);
            this.active_update = null;
        },

        disableScroll() {
            this.is_scroll_disabled = true;
        },

        enableScroll() {
            this.is_scroll_disabled = false;
        },

        scrollToBottom(is_smooth = false) {
            if (this.is_scroll_disabled) return;

            const active_conversation = useConversation().active;
            if (active_conversation?.state?.pending_scroll_to_sid) {
                return;
            }

            const container = document.querySelector(
                ".prospus-messages-container__wrapper",
            );

            if (window.logger) {
                window.logger("[Chat:scrollToBottom] called", {
                    is_smooth,
                    has_container: !!container,
                    scrollTop: container?.scrollTop,
                    scrollHeight: container?.scrollHeight,
                });
            }

            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: is_smooth ? "smooth" : "instant",
                });
            }
        },

        skipChatStreamLoader() {
            this.chat_stream_loader = false;
        },

        continueChatStreamLoader() {
            this.chat_stream_loader = true;
        },

        setMessageEditorFocus() {
            this.is_message_editor_focus = true;
        },

        setMessageEditorOffFocus() {
            this.is_message_editor_focus = false;
        },

        setActiveChecklist() {
            this.is_checklist_active = true;
        },

        setInactiveChecklist() {
            this.is_checklist_active = false;
        },

        toggleChecklist() {
            this.is_checklist_active = !this.is_checklist_active;
        },

        onActivityAdjustStart() {
            this.is_activity_dragged = true;
        },

        onActivityAdjustEnd() {
            this.is_activity_dragged = false;
            this.adjustTextBox();
        },

        setTopbarAvatarActiveMenu(val) {
            this.topbar_avatar_active_menu = val;
        },

        setPreviousTab(tab) {
            this.previous_tab = tab;
        },

        getBackToPreviousTab() {
            this.setTab(this.previous_tab);
        },

        setPreviousConvID(value) {
            this.conv_id_before_open_membership = value;
        },

        setConvIdBeforeStandaloneAssociate(value) {
            this.conv_id_before_standalone_associate = value;
        },

        clearConvIdBeforeStandaloneAssociate() {
            this.conv_id_before_standalone_associate = null;
        },

        setMembershipPrefetchedConversationId(value) {
            this.membership_prefetched_conversation_id = value;
        },

        setMembershipDataLoading(isLoading) {
            this.is_membership_data_loading = Boolean(isLoading);
        },

        toggleLoading(val) {
            this.is_loading = val ?? !this.is_loading;
        },

        setMessageActivity(conversation_id, data, type, value = null) {
            this.removeMessageActivity(conversation_id);

            const value_body = value || data?.body || null;
            this.message_activity.push({
                id: conversation_id,
                type,
                data: data,
                value: value_body,
            });
        },

        removeMessageActivity(conversation_id) {
            const index = this.message_activity.findIndex(
                (item) => item.id === conversation_id,
            );
            if (index !== -1) {
                this.message_activity.splice(index, 1);
            }
        },

        getMessageActivity(conversation_id) {
            return this.message_activity.find(
                (item) => item.id === conversation_id,
            );
        },

        getMessageActivityType(conversation_id, type) {
            return this.message_activity.find(
                (item) => item.id === conversation_id && item.type === type,
            );
        },

        getMessageActivityTypeIndex(conversation_id, type) {
            return this.message_activity.findIndex(
                (item) => item.id === conversation_id && item.type === type,
            );
        },

        updateMessageActivityEditBody(conversation_id, body) {
            const index = this.getMessageActivityTypeIndex(
                conversation_id,
                "edit",
            );
            if (index !== -1) {
                this.message_activity[index].data.body = body;
            }
        },

        updateMessageActivityValue(conversation_id, value, type) {
            const index = this.getMessageActivityTypeIndex(
                conversation_id,
                type,
            );

            if (index !== -1) {
                this.message_activity[index].value = value;
            }
        },
    },
});

function readCssPx(element, property, fallback) {
    const parsed = Number.parseFloat(getComputedStyle(element)[property]);

    return Number.isFinite(parsed) ? parsed : fallback;
}

function measureProseMirrorContentHeight(element) {
    if (!element) {
        return 0;
    }

    const previous_height = element.style.height;
    const previous_min_height = element.style.minHeight;
    element.classList.add("removed-height");
    element.style.height = "auto";
    element.style.minHeight = "0px";
    const height = element.scrollHeight;
    element.style.height = previous_height;
    element.style.minHeight = previous_min_height;
    element.classList.remove("removed-height");

    return height;
}

function composerLayoutMessagesScrollSnapshot(store) {
    return store.messages_scroll_pin_snapshot ?? pinnedMessagesScroll();
}
